import React, {useEffect, useReducer} from 'react';
import {useIntl} from 'react-intl';
import { Lx } from '../../../../../i18n/consts';
import Selector from '../../../../shared/nextgen/selector';
import TSwitch from '../../../../shared/form-controls/switch';
import TypesSelector from '../types-selector';
import {TiminatorAbsenceType, TiminatorType} from '../../../../../features/types/types';
import {FilterActionType, FilterChangeAction, FilterState} from '../filter/types';
import {REmployees} from '../../../../../features/roles/types';
import { PIdNameModel } from '../../../../../features/projects/types';
import {initialStatuses} from '../../../../../features/schedule/slice';
import styles from './property-filter-popup.module.scss'
import FilterListPreview from './filter-list-preview';
import {useAppSelector} from '../../../../../hooks/hooks';
import PropertyFilterPopup from '../../../../shared/property-filter-popup';
import {DropdownWrapper} from '../../../../shared/nextgen/dropdown-wrapper';
import {VisibleDays} from '../../../../../features/report/types';
import formDropdownStyles from '../../../../shared/form-controls/form-dropdown/form-dropdown.module.scss';

interface Props {
    employees: REmployees[];
    projects: PIdNameModel[];
    projectTypes: TiminatorType[];
    shiftTypes: TiminatorType[];
    absenceTypes: TiminatorAbsenceType[];
    onChange: (filter: FilterState) => void;
    disableStatusFilter?: boolean;
    disableEmployeesFilter?: boolean;
    disableAbsenceAndShiftTypesFilter? : boolean;
    disableProjectsFilter?: boolean;
    onSelectedFiltersCountChange?: (count: number) => void;
    showAbsencesToggle?: boolean;
    hasSelected: boolean;
    disabled?: boolean;
    compactNarrowDesktop?: boolean;
    detailedByDay?: boolean;
    onDetailedByDayChange?: (value: boolean) => void;
    visibleDays?: VisibleDays;
    onVisibleDaysChange?: (value: VisibleDays) => void;
}

interface State {
    draft: FilterState;
    current: FilterState;
}

const emptyFilterState: FilterState = {
    selectedEmployees: [],
    selectedProjects: [],
    selectedProjectTypes: [],
    selectedShiftTypes: [],
    selectedAbsenceTypes: [],
    selectedStatuses: [],
    showAbsences:true
}

export default function SchedulePropertyFilterPopup(props: Props) {
    const intl = useIntl();
    const [selectedFiltersCount, setSelectedFiltersCount] = React.useState(0);
    const [currentDetailedByDay, setCurrentDetailedByDay] = React.useState(Boolean(props.detailedByDay));
    const [draftDetailedByDay, setDraftDetailedByDay] = React.useState(Boolean(props.detailedByDay));
    const [currentVisibleDays, setCurrentVisibleDays] = React.useState<VisibleDays>(props.visibleDays ?? VisibleDays.All);
    const [draftVisibleDays, setDraftVisibleDays] = React.useState<VisibleDays>(props.visibleDays ?? VisibleDays.All);
    const filter = useAppSelector(s => s.timeTrackingFilters.filter.propertyFilter);
    const visibleDayOptions = React.useMemo(() => ([
        { id: VisibleDays.All, label: intl.formatMessage({ id: Lx.Report.ALL_DAYS }) },
        { id: VisibleDays.Incorrect, label: intl.formatMessage({ id: Lx.Report.ONLY_INCORRECT_DAYS }) },
    ]), [intl]);

    const initialFilterState: FilterState = {
        ...emptyFilterState,
    }

    const initialState: State = {
        current: initialFilterState,
        draft: initialFilterState,
    }

    const handlePropertyChange = (action: FilterActionType, state: FilterState, payload: any): FilterState => {
        switch (action) {
            case 'EMPLOYEES_UPDATE':
                state.selectedEmployees = payload ?? [];
                break;
            case 'PROJECTS_UPDATE':
                state.selectedProjects = payload ?? [];
                break;
            case 'PROJECTTYPE_UPDATE':
                state.selectedProjectTypes = payload ?? [];
                break;
            case 'SHIFTTYPE_UPDATE':
                state.selectedShiftTypes = payload ?? [];
                break;
            case 'ABSENCETYPE_UPDATE':
                state.selectedAbsenceTypes = payload ?? [];
                break;
            case 'STATUSES_UPDATE':
                state.selectedStatuses = payload ?? [];
                break;
            case 'SHOW_ABSENCES_UPDATE':
                state.showAbsences = typeof payload === 'boolean' ? payload : true;
                break;
            default:
                break;
        }

        return state;
    }


    const reducer = (state: State, action: Partial<FilterChangeAction>): State => {
        const nextState = {...state};

        switch (action.action) {
            case 'HYDRATE': {
                const incoming = (action.payload as FilterState) ?? emptyFilterState;
                nextState.current = {...incoming};
                nextState.draft = {...incoming};
                break;
            }
            case 'APPLY':
                nextState.current = {...state.draft};
                break;
            case 'CANCEL':
                nextState.draft = {...state.current};
                break;
            case 'RESET':
                nextState.draft = {...emptyFilterState};
                break;
            default:
                nextState.draft = handlePropertyChange(action.action as FilterActionType, nextState.draft, (action as any).payload);
                break;
        }

        return nextState;
    }

    const [filterState, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const keys = Object.keys(filterState.current) as (keyof FilterState)[];
        const activeKeys = keys.filter((key) => {
            if (props.disableStatusFilter && key === 'selectedStatuses') return false;
            if (props.disableEmployeesFilter && key === 'selectedEmployees') return false;
            if (props.disableAbsenceAndShiftTypesFilter && (key === 'selectedAbsenceTypes' || key === 'selectedShiftTypes')) return false;
            if (!props.showAbsencesToggle && key === 'showAbsences') return false;
            return true;
        });
        const count = activeKeys.reduce((acc, key) => {
            const value = filterState.current[key];
            if (Array.isArray(value)) {
                return acc + (value.length > 0 ? 1 : 0);
            }
            if (typeof value === 'boolean') {
                return acc + (value ? 0 : 1);
            }
            return acc;
        }, 0) + (currentDetailedByDay ? 1 : 0) + (currentDetailedByDay && currentVisibleDays !== VisibleDays.All ? 1 : 0);

        setSelectedFiltersCount(count);
        props.onSelectedFiltersCountChange?.(count);
    }, [
        currentDetailedByDay,
        filterState.current,
        props.disableAbsenceAndShiftTypesFilter,
        props.disableEmployeesFilter,
        props.disableStatusFilter,
        props.onSelectedFiltersCountChange,
        props.showAbsencesToggle,
    ]);

    useEffect(() => {
        if(props.hasSelected)
            return;
        dispatch({action:'HYDRATE', payload: initialFilterState});
    }, [props.hasSelected]);

    useEffect(() => {
        dispatch({ action: 'HYDRATE', payload: filter });
    }, [filter]);

    useEffect(() => {
        const nextValue = Boolean(props.detailedByDay);
        setCurrentDetailedByDay(nextValue);
        setDraftDetailedByDay(nextValue);
    }, [props.detailedByDay]);

    useEffect(() => {
        const nextValue = props.visibleDays ?? VisibleDays.All;
        setCurrentVisibleDays(nextValue);
        setDraftVisibleDays(nextValue);
    }, [props.visibleDays]);


    const resetFilter = () => {
        dispatch({ action: 'RESET' })
        setDraftDetailedByDay(false);
        setDraftVisibleDays(VisibleDays.All);
    }

    const applyFilter = () => {
        dispatch({ action: 'APPLY' });
        setCurrentDetailedByDay(draftDetailedByDay);
        setCurrentVisibleDays(draftVisibleDays);
        props.onDetailedByDayChange?.(draftDetailedByDay);
        props.onVisibleDaysChange?.(draftVisibleDays);
        props.onChange(filterState.draft);
    }

    const handleClose = () => {
        dispatch({ action: 'CANCEL' });
        setDraftDetailedByDay(currentDetailedByDay);
        setDraftVisibleDays(currentVisibleDays);
    };

    return (
        <PropertyFilterPopup
            selectedFiltersCount={selectedFiltersCount}
            previewContent={
                <FilterListPreview
                    selectedEmployees={props.disableEmployeesFilter ? [] : filterState.current.selectedEmployees}
                    selectedProjects={filterState.current.selectedProjects}
                    selectedProjectTypes={filterState.current.selectedProjectTypes}
                    selectedShiftTypes={filterState.current.selectedShiftTypes}
                    selectedAbsenceTypes={props.disableAbsenceAndShiftTypesFilter ? [] : filterState.current.selectedAbsenceTypes}
                    selectedStatuses={props.disableStatusFilter ? [] : filterState.current.selectedStatuses}
                    detailedByDay={currentDetailedByDay}
                    visibleDays={currentDetailedByDay ? currentVisibleDays : undefined}
                />
            }
            onReset={resetFilter}
            onApply={applyFilter}
            onCancel={handleClose}
            disabled={props.disabled}
            compactNarrowDesktop={props.compactNarrowDesktop}
        >
            {
                !props.disableEmployeesFilter && <Selector id={'employeeFilter'}
                    label={Lx.General.EMPLOYEES}
                    items={props.employees.map((employee) => ({ id: employee.id, name: employee.name, originalId: employee.id }))}
                    name={Lx.General.EMPLOYEES}
                    selected={filterState.draft.selectedEmployees}
                    maxWidth={368}
                    setSelectedValues={(values) => dispatch({ action: 'EMPLOYEES_UPDATE', payload: values })} intl={intl}/>
            }

            {
                !props.disableStatusFilter && <Selector id={'statusFilter'}
                    label={Lx.General.STATUS}
                    items={initialStatuses.map((x) => ({...x, name: intl.formatMessage({ id: x.name })}))}
                    name={Lx.General.STATUS}
                    selected={filterState.draft.selectedStatuses}
                    maxWidth={368}
                    setSelectedValues={(values) => dispatch({ action: 'STATUSES_UPDATE', payload: values })}
                    intl={intl}/>
            }
            {
                !props.disableProjectsFilter && <Selector id={'projectsFilter'}
                    label={Lx.General.PROJECTS}
                    items={props.projects.map((project) => ({ id: project.id, name: project.name, originalId: project.id }))}
                    name={Lx.General.PROJECTS}
                    selected={filterState.draft.selectedProjects}
                    maxWidth={368}
                    setSelectedValues={(values) => dispatch({ action: 'PROJECTS_UPDATE', payload: values })}
                    intl={intl}/>
            }
            {
                <Selector id={'projectTypesFilter'}
                    label={Lx.General.PROJECT_TYPES}
                    items={props.projectTypes.map((projectType) => ({ id: projectType.id, name: projectType.name, originalId: projectType.id }))}
                    name={Lx.General.PROJECT_TYPES}
                    selected={filterState.draft.selectedProjectTypes}
                    maxWidth={368}
                    setSelectedValues={(values) => dispatch({ action: 'PROJECTTYPE_UPDATE', payload: values })}
                    intl={intl}/>
            }
            {
                !props.disableAbsenceAndShiftTypesFilter && <TypesSelector selectedShiftTypes={filterState.draft.selectedShiftTypes}
                    selectedAbsenceTypes={filterState.draft.selectedAbsenceTypes}
                    shiftTypes={props.shiftTypes}
                    absenceTypes={props.absenceTypes}
                    onShiftTypesSelected={(values) => dispatch({ action: 'SHIFTTYPE_UPDATE', payload: values })}
                    onAbsenceTypesSelected={(values) => dispatch({ action: 'ABSENCETYPE_UPDATE', payload: values })}
                    intl={intl}
                    maxWidth={368}/>
            }
            {
                props.showAbsencesToggle && (<TSwitch
                    label={intl.formatMessage({ id: Lx.Report.SHOW_ABSENCES })}
                    name={'showAbsences'}
                    checked={filterState.draft.showAbsences}
                    onChange={(value) => dispatch({ action: 'SHOW_ABSENCES_UPDATE', payload: value })}
                    labelProps={{ className: styles.absencesSwitchLabel }}
                />)
            }
            {
                props.onDetailedByDayChange && (
                    <>
                        <TSwitch
                            label={'Detailed by day'}
                            name={'detailedByDay'}
                            checked={draftDetailedByDay}
                            onChange={setDraftDetailedByDay}
                            labelProps={{ className: styles.absencesSwitchLabel }}
                        />
                        {draftDetailedByDay && (
                            <div className={styles.dropdownBlock}>
                                <div className={styles.dropdownLabel}>Show days</div>
                                <div className={styles.dropdownField}>
                                    <DropdownWrapper
                                        className={`${formDropdownStyles.dropdown} ${styles.visibleDaysDropdown}`}
                                        name={'visibleDays'}
                                        value={draftVisibleDays}
                                        options={visibleDayOptions}
                                        showCheckIcon
                                        selectedTextColor='secondary'
                                        onChange={(_, value) => setDraftVisibleDays((value ?? VisibleDays.All) as VisibleDays)}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )
            }
        </PropertyFilterPopup>
    )
}
